/**
 * What a catalog card says about its model, read from the model's definition as the app reads it:
 * the name (FurnitureVariation.GetBriefText for a gallery tile: the brand and collection in bold,
 * the caption after them), whether the definition sets the model free or PRO (`Free`), and when it
 * was added (`Added`, which decides NEW). The catalog's search answer does not carry these: most of
 * the app's own models come without a title, and a person's title is not what the app shows.
 *
 * A card is the model's first variation, the one a tap places.
 */
import { attr, kids, parseXml, type Node } from '../core/hp/xml';
import { language } from '../i18n/I18n';

export interface CardDefinition {
  tags: string[];
  /** The root `Caption`: the app's FurnitureInfo.DefaultCaption. */
  caption: string;
  /** The root `CaptionsUI` in the interface language (FurnitureInfo.UICaption), when the definition has one. */
  uiCaption?: string;
  brand?: string;
  /** The first variation's collections, else the root's (FurnitureVariation.Collections). */
  collections: string[];
  /** The first variation's own caption, a colour most of the time. */
  variationCaption: string;
  /** `Free`: true sets the model free, false makes it PRO (FurnitureVariation.IsFree). */
  free?: boolean;
  /** `Added`, as written (yyyy-mm-dd). */
  added?: string;
  /** The first variation's first material (FurnitureVariation.MaterialInfo): what the app builds a wall of. */
  material?: { type?: string; color?: string };
  /** The first variation's `Id`, even an only one: the variation the app's piece wears once its definition loads (FurnitureVariation.Load). */
  firstVariationId?: string;
}

/** FurnitureVariation.ParseCollectionStr. */
const collectionsOf = (value: string | undefined) => (value ?? '').split(/[,/]/).map((part) => part.trim()).filter(Boolean);

/**
 * FurnitureInfo.UICaption: the interface language's part of `ar:…%,%bg:…%,%…`; where it is missing, English's, then
 * the first part.
 */
export function uiCaptionOf(value: string | undefined): string | undefined {
  const parts = (value ?? '').split('%,%').filter(Boolean);
  const of = (code: string) => parts.find((part) => part.startsWith(code + ':'))?.slice(code.length + 1) || undefined;
  return of(language()) ?? of('en') ?? (parts[0]?.slice(parts[0].indexOf(':') + 1) || undefined);
}

export function readCardDefinition(xml: string): CardDefinition | null {
  let parsed: Node;
  try { parsed = parseXml(xml.replace(/^\uFEFF/, '')); } catch { return null; }
  const root: Node | undefined = parsed?.Item && typeof parsed.Item === 'object' ? parsed.Item : undefined;
  if (!root) return null;
  const first = kids(kids(root, 'Variations')[0], 'Item')[0];
  const free = attr(first, 'Free') ?? attr(root, 'Free');
  // A catalog definition nests the slots in the variation; the app's own resources write a one-slot variation flat.
  const slot = kids(first, 'Item')[0] ?? first;
  const materialType = attr(slot, 'Material') ?? attr(slot, 'Type'), materialColor = attr(slot, 'Color'), firstVariationId = attr(first, 'Id');
  return {
    tags: (attr(root, 'Tags') ?? '').split(',').map((tag) => tag.trim()).filter(Boolean),
    caption: attr(root, 'Caption') ?? '',
    uiCaption: uiCaptionOf(attr(root, 'CaptionsUI')),
    brand: attr(first, 'Brand') ?? attr(root, 'Brand') ?? undefined,
    collections: collectionsOf(attr(first, 'Collection') ?? attr(root, 'Collection')),
    variationCaption: attr(first, 'Caption') ?? attr(root, 'Caption') ?? '',
    ...(free !== undefined ? { free: free.toLowerCase() === 'true' } : {}),
    ...(attr(root, 'Added') ? { added: attr(root, 'Added') } : {}),
    ...(materialType || materialColor ? { material: { ...(materialType ? { type: materialType } : {}), ...(materialColor ? { color: materialColor } : {}) } } : {}),
    ...(firstVariationId ? { firstVariationId } : {}),
  };
}

/** Config.xml `/Config@AbstractPicture` and `@PlaneDecor`: what FurnitureInfo.IsMultiproductVariation reads. */
const ABSTRACT_PICTURE = new Set(['Painting', 'Poster', 'Sticker', 'Flag', 'Appliances', 'ElevatorPanel', 'Objects2D', 'AbstractPicture', 'Annotation']);
const PLANE_DECOR = new Set(['2DPool', 'Drain', 'Roads', 'Luke', 'PlaneElectronics', 'SurfaceDecor', 'CosmeticsFlat', 'Rug']);
const multiproduct = (tags: string[]) => tags.some((tag) => tag === 'Carpet' || tag === 'CeilingBeams' || PLANE_DECOR.has(tag) || ABSTRACT_PICTURE.has(tag));

export interface CardName { bold: string; rest: string }

/**
 * FurnitureVariation.GetBriefText(isListItem: true, withoutColor: true) and FurnitureListItem
 * .SetCaption: the bold title is the first word group (with the brand, the one after it too), the
 * rest the caption after it. `translate` is the app's I18n.TranslateBruteForce of a caption.
 */
export function briefName(definition: CardDefinition, translate: (caption: string) => string = (caption) => caption): CardName {
  const tokens: string[] = [];
  const add = (text: string | undefined) => { if (text && !tokens.includes(text)) tokens.push(text); };
  const brand = definition.brand;
  add(brand);
  for (const collection of definition.collections) {
    if (brand && (brand.includes(collection) || collection.includes(brand))) continue;
    add(collection);
  }
  const { caption: defaultCaption, variationCaption: caption } = definition;
  const colorAsTitle = multiproduct(definition.tags) && definition.collections.length === 0;
  if (colorAsTitle && defaultCaption !== caption) add(translate(caption));
  if (definition.uiCaption) tokens.push(definition.uiCaption);
  else add(translate(defaultCaption));
  if (!colorAsTitle && defaultCaption !== caption && tokens.length < 3) add(translate(caption));
  const hasColorTitle = multiproduct(definition.tags) ? definition.collections.length > 0 : !!defaultCaption && defaultCaption !== caption;
  while (tokens.length < 2) tokens.push('');
  const title = [tokens[0]];
  if (brand && tokens.length > 1) title.push(tokens[1]);
  const texts = tokens.slice(title.length, tokens.length - (hasColorTitle ? 1 : 0));
  return { bold: title.filter(Boolean).join(' '), rest: texts[0] ?? '' };
}

// ---------------------------------------------------------------------------------------------
// Definitions, one request per file however many cards ask, a few at a time.

const known = new Map<string, Promise<CardDefinition | null>>();
/** The reads that failed, as against a card that holds no definition: both answer null. */
const failed = new WeakSet<Promise<CardDefinition | null>>();
/** What is already read: a card drawn again has its name on the first paint. */
const settled = new Map<string, CardDefinition | null>();
const waiting: Array<() => void> = [];
let running = 0;
const PARALLEL = 8;

type Fetch = typeof fetch;
let fetchImpl: Fetch | null = null;
/** Tests hand in their own fetch; the page uses the browser's. */
export function useCardFetch(impl: Fetch | null): void { fetchImpl = impl; known.clear(); settled.clear(); }

async function slot<T>(work: () => Promise<T>): Promise<T> {
  while (running >= PARALLEL) await new Promise<void>((resolve) => waiting.push(resolve));
  running++;
  try { return await work(); } finally { running--; waiting.shift()?.(); }
}

const readable = (url: string | undefined): url is string => !!url && /^https?:\/\//.test(url);

/**
 * The definition behind a card, or null when there is none or it cannot be read: the card then
 * keeps the catalog's title and the marks its document gives. A read that failed is asked again by the next call.
 */
export function cardDefinition(url: string | undefined): Promise<CardDefinition | null> {
  if (!readable(url)) return Promise.resolve(null);
  let entry = known.get(url);
  if (!entry) {
    const read: Promise<CardDefinition | null> = slot(async () => {
      const response = await (fetchImpl ?? fetch)(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return readCardDefinition(await response.text());
    }).catch(() => {
      if (known.get(url) === read) known.delete(url);
      failed.add(read);
      return null;
    }).then((definition) => { settled.set(url, definition); return definition; });
    known.set(url, entry = read);
  }
  return entry;
}

/** The definition when it is already read; undefined while it is not, null when there is none. */
export function cardDefinitionNow(url: string | undefined): CardDefinition | null | undefined {
  return readable(url) ? settled.get(url) : null;
}

/**
 * Whether a tap on the card may place it: false only when the read of its definition failed, whose PRO mark and caption
 * would be guessed. A card with no definition, or one that holds nothing readable, goes on with the catalog's own.
 */
export function cardDefinitionRead(url: string | undefined): Promise<boolean> {
  const read = cardDefinition(url);
  return read.then(definition => definition !== null || !failed.has(read));
}

/** Whether a card is to ask for its definition: never asked yet, or its last read failed. */
export function cardDefinitionDue(url: string | undefined): boolean {
  return readable(url) && !known.has(url);
}
